import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, Compass, Navigation, Sparkles } from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import personasData from '@/data/personas.json';

interface Props {
    guidedStudy: GuidedStudy;
    allStudies: GuidedStudy[];
    onPersonaSwitch: (personaId: string) => void;
    isDarkMode: boolean;
}

export function GuidedStudyPersonaSelector({
    guidedStudy,
    allStudies,
    onPersonaSwitch,
    isDarkMode
}: Props) {
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-auto p-0 hover:bg-transparent mb-0.5 pl-0 -ml-0.5 flex items-center gap-2 group max-w-full overflow-hidden">
                    <div className="p-1.5 rounded-lg bg-primary/10 text-primary group-hover:bg-primary/20 transition-colors shrink-0">
                        <Compass className="h-4 w-4" />
                    </div>
                    <h2 className="text-base font-bold tracking-tight truncate line-clamp-1 flex-1">{guidedStudy.title}</h2>
                    <ChevronDown className="h-4 w-4 text-muted-foreground/50 shrink-0" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-96 max-h-[80vh] overflow-y-auto">
                {allStudies.map((study) => {
                    const normalizedPersonaId = study.personaId;
                    const persona = personasData.personas.find(p => p.id === normalizedPersonaId);
                    const isCurrent = study.personaId === guidedStudy.personaId;

                    if (!persona) return null;

                    return (
                        <DropdownMenuItem
                            key={study.personaId}
                            onClick={() => !isCurrent && onPersonaSwitch(study.personaId)}
                            className={`flex-col items-start gap-2 p-4 cursor-pointer border-b last:border-0 ${isCurrent ? 'bg-primary/5 opacity-80' : 'hover:bg-primary/5'
                                } `}
                            disabled={isCurrent}
                        >
                            <div className="flex items-center justify-between w-full">
                                <div className="flex items-center gap-2">
                                    <div className={`p-1.5 rounded-md ${isCurrent ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'} `}>
                                        <Navigation className="h-3.5 w-3.5" />
                                    </div>
                                    <div>
                                        <div className="font-bold text-sm tracking-tight">{persona.title}</div>
                                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Expedition Role</div>
                                    </div>
                                </div>
                                {isCurrent && (
                                    <Badge variant="default" className="text-[9px] h-4 uppercase tracking-tighter">Active Role</Badge>
                                )}
                            </div>
                            <div className={`text-xs leading-relaxed italic border-l-2 pl-3 py-1 ${isDarkMode ? 'border-amber-500/30 text-slate-400' : 'border-primary/20 text-muted-foreground'} `}>
                                <b>{study.title}</b><br />
                                "{study.hook}"
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                                <div className="text-[10px] text-muted-foreground font-medium flex items-center gap-1">
                                    <Sparkles className="h-3 w-3" />
                                    Lens: {persona.targetAudience}
                                </div>
                            </div>
                        </DropdownMenuItem>
                    );
                })}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
